import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Button, Alert, Tabs } from 'antd';
import * as _ from 'lodash';
import { Icon } from 'dlweb-components';
import TemplateBrowser from './TemplateBrowser';
import DocumentBrowser from './DocumentBrowser';
import FavoritesBrowser from './FavoritesBrowser';
import Dropzone from '../Dropzone';
import { fileService } from '../../services/fileService';

const FileBrowser = ({workingFolder, onSelect, onFileDrop, permissions, templates, hasTemplates, onTemplateToggle, loadingTemplates}) => {

  const wrapRef = useRef(null);

  const [showAlert, setShowAlert] = useState(false);
  const [selectedTab, setSelectedTab] = useState('1');
  const [selectedFolder, setSelectedFolder] = useState('Documents');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState({});
  const [templatesRetricted, setTemplatesRestricted] = useState(false);
  const [importRestricted, setImportRestricted] = useState(false);
  const [tableHeight, setTableHeight] = useState(0);

  const onFolderClick = (folderId) => {
    setSelectedFolder(folderId);
    setSelectedTab('2');
  }

  useEffect(() => {
  const updateTableHeight = () => {
    if (wrapRef.current) {
      const wrapRect = wrapRef.current.getBoundingClientRect();
      const available = wrapRect.height - 170;
      setTableHeight(available);
    }
  };

  updateTableHeight();
  window.addEventListener('resize', updateTableHeight);
  return () => window.removeEventListener('resize', updateTableHeight);
}, []);

  useEffect(() => {
    setTemplatesRestricted(permissions?.restrictProfilesAndTemplates == 1);
    setImportRestricted(permissions?.allowImport == 0);
  }, [permissions])

  const onTemplateSelect = record => {

    const id = record.id || record.ID;
    const name = record.name || record.FileName;
    const type = record.type || record.ObjectType;

    const template = {
      id: id,
      name: `${name}${type ? '.' + type : ''}`
    }

    setSelectedRowKeys([id]);
    setSelectedTemplate(template);
  }

  useEffect(() => {
    selectedRowKeys.length > 0 && setShowAlert(true);
  }, [selectedRowKeys])

  const closeAlert = () => {
    setShowAlert(false);
    setSelectedRowKeys([]);
  }

  const onTabChange = key => {
    closeAlert();
    setSelectedTab(key);
  }

  const onUseButtonClick = () => {
    closeAlert();
    onSelect(selectedTemplate);
  }

  const showLocalFilePicker = async () => {

    try {
      const pickerOptions = {
        multiple: false,
        excludeAcceptAllOption: false
      };
  
      const fileHandle = await fileService.openFileBrowser(pickerOptions);
      const file = await fileHandle.getFile();
      onFileDrop(file);
    }
    catch (err) {
      // do nothing - we get an error when the use cancels the file picker
    }
  }

  return (
    <TabWrapper ref={wrapRef}>
      <Tabs
        type='card'
        activeKey={selectedTab}
        style={{margin: 0}}
        onChange={onTabChange}
        items={[
          {
            label: <div>
                    <Icon type='file-invoice' style={{marginRight: '10px'}}/>
                      Templates
                  </div>,
            key: '1',
            children: (
              <TemplateBrowser
                wrapRef={wrapRef}
                permissions={permissions}
                workingFolder={workingFolder}
                selectedRowKeys={selectedRowKeys}
                onTemplateSelect={onTemplateSelect}
                templatesRetricted={templatesRetricted}
                onDoubleClick={onUseButtonClick}
                templates={templates}
                hasTemplates={hasTemplates}
                onTemplateToggle={onTemplateToggle}
                loadingTemplates={loadingTemplates}
              />
            ),
          },
          {
            label: <div>
                    <Icon type='file' style={{marginRight: '10px'}}/>
                      Documents
                  </div>,
            key: '2',
            disabled: templatesRetricted,
            children: (
              <DocumentBrowser
                tableHeight={tableHeight}
                folderId={selectedFolder}
                onFolderClick={onFolderClick}
                selectedRowKeys={selectedRowKeys}
                onTemplateSelect={onTemplateSelect}
                onDoubleClick={onUseButtonClick}
              />
            ),
          },
          {
            label: <div>
                    <Icon type='star' style={{marginRight: '10px'}}/>
                      Favorites
                  </div>,
            key: '4',
            disabled: templatesRetricted,
            children: (
              <FavoritesBrowser
                tableHeight={tableHeight}
                onFolderClick={onFolderClick}
                selectedRowKeys={selectedRowKeys}
                onTemplateSelect={onTemplateSelect}
                onDoubleClick={onUseButtonClick}
              />
            ),
          },
          {
            label: <div>
                    <Icon type='file-upload' style={{marginRight: '10px'}}/>
                      Upload
                  </div>,
            key: '3',
            disabled: importRestricted,
            children:   <Dropzone
                          onFileDrop={onFileDrop}
                          style={{padding: '15px', height: '100%'}}
                        >
                          <DropArea onClick={showLocalFilePicker}>
                            <div>
                              <Icon type='file-upload' style={{fontSize: '75px'}}/>
                            </div>
                            <div>Drag file here or click to browse</div>
                          </DropArea>
                        </Dropzone>,
          },
        ]}
      />
          {showAlert &&
            <AlertContainer>
              <Alert
                type="success"
                closable={true}
                onClose={closeAlert}
                message={(
                  <div style={{fontSize: '16px', marginLeft: '40px'}}>
                    1 document selected
                  </div>
                )}
                action={
                  <Button onClick={onUseButtonClick}>
                    Use selected document
                  </Button>
                }
                style={{
                  borderTopLeftRadius: '0',
                  borderTopRightRadius: '0',
                  border: '1px solid #6895c0',
                  background: '#6895c0',
                  color: '#fff',
                  // boxShadow: '0 -2px 5px 0px #888'
                }}
              />
            </AlertContainer>
          }
    </TabWrapper>
)};

const TabWrapper = styled.div`
  overflow: hidden;
  height: calc(100% - 5px);
  .ant-tabs {
    height: 100%;
  }
  .ant-table-body {
    overflow: auto;
    /* height: calc(100vh - 800px) !important; */
  }
  .ant-tabs-content-holder {
    padding: 0 !important;
  }
  .ant-tabs-top > .ant-tabs-nav {
    margin: 0;
  }
  #wt-breadcrumb-wrapper {
    border-bottom: none;
  }
  .ant-table-selection-col,
  .ant-table-selection-column,
  .ant-table-measure-row td:first-child
  {
    display: none;
  }
  .ant-tabs-content {
    overflow: hidden;
  }
`

const DropArea = styled.div`
  color: #a5a5a5;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  background-color: #fcfcfc;
  border: 2px dashed #d9d9d9;
  font-size: 16px;
  border-radius: 5px;
  transition: border-color 0.3s;
  &:hover {
    border-color: #2269a4;
  }
`

const AlertContainer = styled.div`
  padding: 0 10px;
  position: absolute;
  bottom: 0px;
  left: 0;
  width: 100%;
  animation: fade-in .2s linear;
  .ant-alert-close-icon {
    font-size: 16px;
    position: absolute;
    top: 17px;
    left:6px;
    color: #fff;
    .anticon {
      color: #fff;
    }
  }

  @keyframes fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

export default FileBrowser;